import DOMPurify from 'dompurify';
import Loader from '../Loader';
import logger from '../../utils/logger';
import React, { useMemo } from 'react';
import { Box, BoxProps, ClickAwayListener } from '@mui/material';
import { catchError, from, of, Subject, switchMap } from 'rxjs';
import { currentLocationStatus } from '../../constants/error';
import { CurrentLocationStatus } from '../../types/location';
import { ERROR, HELPER_TEXT } from '../../constants/autocomplete';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import {
  ClearButton,
  ClearContainer,
  GroupHeader,
  GroupItem,
  StartIconContainer,
  MainContainer,
  RootContainer,
  StyledFieldContainer,
  StyledInput,
  StyledLi,
  StyledOptions,
  StyledTypography,
  EndIconContainer,
  HelperTextContainer,
  OptionItemContainer,
} from './AutocompleteStyle';
import {
  AutocompleteHelperTextPosition,
  AutocompleteOptionGroup,
  AutocompleteOptionType,
  AutocompletePopupPosition,
  AutocompleteStandardOption,
  OnAutocompleteInputChange,
  AutocompleteSelectableOption,
  UpdateAutocompleteSubjectType,
} from '../../types/autocomplete';

/**
 * Autocomplete Component
 *
 * This component serves as a base Autocomplete component that can be extended to provide
 * different results, such as locations, equipment, or equipment categories, by providing
 * suitable handlers to fetch different kinds of data based on user input.
 *
 * @interface AutocompleteProps
 * @property {AutocompleteOption | null} value - The current persisted value.
 * @property {boolean} isLoading - Indicates if the component is in a loading state.
 * @property {JSX.Element} [startIcon] - Optional icon or visual element before the input field.
 * @property {JSX.Element} [endIcon] - Optional icon or visual element at the end of the input field.
 * @property {AutocompletePopupPosition} [popupPosition] - Position of the options dropdown.
 * @property {string} placeholder - Specifies the placeholder for the location autocomplete component.
 * @property {(option: AutocompleteOption) => Promise<void>} selectHandler - Callback for option selection.
 * @property {(value: string) => Promise<AutocompleteOptionGroup[]>} getOptionsHandler - Callback to fetch suggestions.
 * @property {RxJS Subject} closeAutocompleteSubject - Helps to close the autocomplete programatically from outside the component.
 * @property {(value: boolean) => Promise<void>} onDropdownToggleFn - Callback function to let parent component perform actions on dropdown becomes visible/hidden.
 *
 *
 * Autocomplete Dropdown Option Types:
 *
 * Autocomplete options can be of the following types-
 * - [type definition AutocompleteStandardOption]: Defines a standard string line item to be rendered in the dropdown.
 * - [type definition AutocompleteJSXOption]: Definition for JSX Element to be rendered in the dropdown.
 * - Events on string values are handled using the [selectHandler] prop passed from the parent.
 *
 *
 *
 * Autocomplete Dropdown Option Types:
 *
 * Autocomplete options can be of the following types-
 * - [type definition AutocompleteStandardOption]: Defines a standard string line item to be rendered in the dropdown.
 * - [type definition AutocompleteJSXOption]: Definition for JSX Element to be rendered in the dropdown.
 * - Events on string values are handled using the [selectHandler] prop passed from the parent.
 *
 *
 *
 * Autocomplete Dropdown Option Types:
 *
 * Autocomplete options can be of the following types-
 * - [type definition AutocompleteStandardOption]: Defines a standard string line item to be rendered in the dropdown.
 * - [type definition AutocompleteJSXOption]: Definition for JSX Element to be rendered in the dropdown.
 * - Events on string values are handled using the [selectHandler] prop passed from the parent.
 *
 *
 * @example
 * // Usage example:
 * <Autocomplete
 *   value={selectedLocation}
 *   isLoading={loading}
 *   startIcon={<LocationIcon />}
 *   popupPosition="bottom"
 *   selectHandler={handleSelect}
 *   getOptionsHandler={fetchOptions}
 * />
 */

// TODO: Optimize the props
interface AutocompleteProps extends BoxProps {
  value: AutocompleteStandardOption | null;
  setValue?: React.Dispatch<React.SetStateAction<AutocompleteStandardOption | null>>;
  isLoading: boolean;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  submitOnEndIconClick?: boolean;
  clearLabel?: JSX.Element;
  popupPosition?: AutocompletePopupPosition;
  placeholder?: string;
  helperText?: JSX.Element | null;
  isCheckoutFlow?: boolean;
  helperTextPosition?: AutocompleteHelperTextPosition;
  selectHandler: (option: AutocompleteSelectableOption) => Promise<void>;
  getOptionsHandler: (value: string) => Promise<AutocompleteOptionGroup[]>;
  inputFocusSubject?: Subject<boolean>;
  closeAutocompleteSubject?: Subject<boolean>;
  updateAutocompleteSubject?: UpdateAutocompleteSubjectType;
  onDropdownToggleFn?: (value: boolean) => void;
  overRideOnEnterInInputField?: (value: string) => void;
  onInputChangeCallBack?: (value: string) => void;
  resetCurrentLocationOnClickAway?: boolean;
  isAddressAutocomplete?: boolean;
  setAddress?: (value: string) => void;
  triggerAddressValidation?: () => void;
  hideHelperTextContainer?: boolean;
  onInputChange?: OnAutocompleteInputChange;
  disabled?: boolean;
}

interface ActiveOption {
  groupIndex: number;
  itemIndex: number;
}

function Autocomplete({
  value,
  setValue,
  isLoading,
  startIcon,
  endIcon,
  submitOnEndIconClick,
  clearLabel,
  popupPosition,
  placeholder,
  helperText,
  helperTextPosition,
  isCheckoutFlow,
  selectHandler,
  getOptionsHandler,
  inputFocusSubject,
  closeAutocompleteSubject,
  updateAutocompleteSubject,
  onDropdownToggleFn,
  overRideOnEnterInInputField,
  onInputChangeCallBack,
  resetCurrentLocationOnClickAway,
  isAddressAutocomplete,
  setAddress,
  triggerAddressValidation,
  hideHelperTextContainer,
  onInputChange,
  disabled = false,
  ...rest
}: AutocompleteProps) {
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<AutocompleteOptionGroup[]>([]);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [activeOption, setActiveOption] = useState<ActiveOption | null>(null);
  const [rightPaddingValue, setRightPaddingValue] = useState<string | null>(null);
  const [showChangeButton, setShowChangeButton] = useState<boolean | null>(null);

  // Subject for managing promises resolving to dropdown options values.
  const optionsSubject = useMemo(() => new Subject<Promise<AutocompleteOptionGroup[]>>(), []);

  const rootRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const clearContainerRef = useRef<HTMLInputElement>(null);
  const mainContainerRef = useRef<HTMLElement>(null);

  const sanitizedData = (option: string) => ({
    __html: DOMPurify.sanitize(option),
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isAddressAutocomplete && setValue) {
      setValue({ id: uuid(), label: event.target.value, type: AutocompleteOptionType.STANDARD });
      if (setAddress) setAddress(event.target.value);
    }
    setInputValue(event.target.value);
    onInputChange && onInputChange(event.target.value, { isActive: popupOpen });

    if (event.target.value) currentLocationStatus.next(CurrentLocationStatus.NONE);
  };

  const handleSelect = async (option: AutocompleteSelectableOption) => {
    await selectHandler(option);
    setOptions([]);
    setPopupOpen(false);
  };

  const handleChangeClick = () => {
    setInputValue('');
    setOptions([]);
    setPopupOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Home':
        event.preventDefault();
        if (inputValue && popupOpen && options.length) {
          setFirstOptionAsActive();
        }
        break;
      case 'End':
        event.preventDefault();
        if (inputValue && popupOpen && options.length) {
          setLastOptionAsActive();
        }
        break;
      case 'PageUp':
        event.preventDefault();
        if (inputValue && popupOpen && options.length) {
          setPrevOptionAsActive();
        }
        break;
      case 'PageDown':
        event.preventDefault();
        if (inputValue && popupOpen && options.length) {
          setNextOptionAsActive();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (inputValue && popupOpen && options.length) {
          setNextOptionAsActive();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (inputValue && popupOpen && options.length) {
          setPrevOptionAsActive();
        }
        break;
      case 'Enter':
        event.preventDefault();
        handleAutoSelect();
        break;
      case 'Escape':
        event.preventDefault();
        exitAutocomplete(true);
        break;
      case 'Tab':
        if (isAddressAutocomplete) {
          if (triggerAddressValidation) triggerAddressValidation();
          exitAutocomplete(true);
        }
        break;
      default:
    }
  };

  const handleEndIconClick = () => {
    submitOnEndIconClick && handleAutoSelect();
  };

  const handleAutoSelect = () => {
    if (overRideOnEnterInInputField && activeOption === null) {
      overRideOnEnterInInputField(inputValue);
    } else if (inputValue && popupOpen && options.length) {
      const currentGroup = options[activeOption?.groupIndex ?? 0];
      const currentItem = currentGroup.groupItems[activeOption?.itemIndex ?? 0];
      if (currentItem?.type === AutocompleteOptionType.STANDARD) {
        handleSelect(currentItem);
      }
    }
  };

  // Emits a promise to optionsSubject based on user input,
  // which resolves to getOptionsHandler response or an empty array.
  const getOptions = async (value: string) => {
    setActiveOption(null);
    value = value.trim();
    optionsSubject.next(getOptionsHandler(value));
  };

  const exitAutocomplete = (setFocusSubjectToFalse?: boolean) => {
    setInputValue(value?.label ?? '');
    setPopupOpen(false);
    setActiveOption(null);
    inputRef.current?.blur();
    if (inputFocusSubject && setFocusSubjectToFalse) {
      inputFocusSubject.next(false);
    }
    if (resetCurrentLocationOnClickAway) currentLocationStatus.next(CurrentLocationStatus.NONE);
  };

  const showTextField = () => {
    return (!popupOpen && value) || isLoading;
  };

  const setNextOptionAsActive = () => {
    setActiveOption((prev) => {
      if (!prev) {
        return { groupIndex: 0, itemIndex: 0 };
      }
      const currentGroupItems = options[prev.groupIndex].groupItems;
      const isLastItem = prev.itemIndex === currentGroupItems.length - 1;
      const isLastGroup = prev.groupIndex === options.length - 1;
      return {
        groupIndex: isLastItem ? (isLastGroup ? 0 : prev.groupIndex + 1) : prev.groupIndex,
        itemIndex: isLastItem ? 0 : prev.itemIndex + 1,
      };
    });
  };

  const setPrevOptionAsActive = () => {
    setActiveOption((prev) => {
      if (!prev) {
        return { groupIndex: 0, itemIndex: 0 };
      }
      const currentGroupItems = options[prev.groupIndex].groupItems;
      const isFirstItem = prev.itemIndex === 0;
      const isFirstGroup = prev.groupIndex === 0;
      return {
        groupIndex: isFirstItem
          ? isFirstGroup
            ? options.length - 1
            : prev.groupIndex - 1
          : prev.groupIndex,
        itemIndex: isFirstItem ? currentGroupItems.length - 1 : prev.itemIndex - 1,
      };
    });
  };

  const setFirstOptionAsActive = () => {
    setActiveOption({ groupIndex: 0, itemIndex: 0 });
  };

  const setLastOptionAsActive = () => {
    const groupIndex = options.length - 1;
    const itemIndex = options[options.length - 1].groupItems.length - 1;
    setActiveOption({ groupIndex, itemIndex });
  };

  const paddingRightValue = () => {
    return rightPaddingValue
      ? rightPaddingValue
      : showChangeButton
      ? '0px'
      : endIcon
      ? '40px'
      : '0px';
  };

  const paddingLeftValue = () => {
    return startIcon ? '40px' : '0px';
  };

  const focusOnInput = () => {
    inputRef.current?.focus();
    inputFocusSubject?.next(true);
  };

  useEffect(() => {
    const width = (clearContainerRef.current as HTMLDivElement)?.offsetWidth;
    if (width) {
      setRightPaddingValue(width + 'px');
    } else {
      setRightPaddingValue(null);
    }
    const inputFocusSubscription = inputFocusSubject?.subscribe((e: boolean) => {
      setTimeout(() => {
        setPopupOpen(e);
      }, 0);
    });

    const closeAutocompleteSubscription = closeAutocompleteSubject?.subscribe((e: boolean) => {
      setTimeout(() => {
        if (e) exitAutocomplete(true);
      }, 0);
    });

    const updateAutocompleteSubscription = updateAutocompleteSubject?.subscribe((e) => {
      setTimeout(() => {
        if (typeof e.nextInputValue === 'string') {
          setInputValue(e.nextInputValue);
        }
        if (e.nextOptions) {
          optionsSubject.next(e.nextOptions);
        }
      }, 0);
    });

    return () => {
      inputFocusSubscription?.unsubscribe();
      closeAutocompleteSubscription?.unsubscribe();
      updateAutocompleteSubscription?.unsubscribe();
    };
  }, []);

  // Listen to optionsSubject stream and set dropdown options value
  useEffect(() => {
    const optionsSubjectSubscription = optionsSubject
      ?.pipe(
        switchMap((e: Promise<AutocompleteOptionGroup[]>) => {
          return from(e).pipe(
            catchError((error) => {
              logger('error', 'at resolving get options promise', error);
              return of([]);
            }),
          );
        }),
      )
      .subscribe((e: AutocompleteOptionGroup[]) => {
        setOptions(e);
      });

    return () => optionsSubjectSubscription?.unsubscribe();
  }, []);

  useEffect(() => {
    if (value && !popupOpen) {
      setInputValue(value.label);
    }
  }, [value]);

  useEffect(() => {
    if (inputRef.current !== document.activeElement) {
      return;
    }
    getOptions(inputValue);
    if (onInputChangeCallBack) {
      onInputChangeCallBack(inputValue);
    }
  }, [inputValue.trim()]);

  useEffect(() => {
    if (popupOpen) {
      inputRef.current?.focus();
      getOptions(inputValue);
    }
  }, [popupOpen]);

  useEffect(() => {
    if (onDropdownToggleFn) {
      onDropdownToggleFn(popupOpen && options.length > 0);
    }
  }, [popupOpen, options]);

  useEffect(() => {
    setShowChangeButton(
      !!value && !popupOpen && !isLoading && !!clearLabel && !isAddressAutocomplete,
    );
  }, [value, popupOpen, isLoading, clearLabel]);

  useEffect(() => {
    if (value && value.label) {
      setInputValue(value.label);
    }
  }, [value]);

  return (
    <ClickAwayListener onClickAway={() => exitAutocomplete(false)}>
      <RootContainer ref={rootRef}>
        <MainContainer
          ref={mainContainerRef}
          paddingRightValue={paddingRightValue()}
          paddingLeftValue={paddingLeftValue()}
          {...rest}
        >
          {startIcon && (
            <StartIconContainer
              className='autocomplete-start-icon-container'
              onClick={focusOnInput}
            >
              {startIcon}
            </StartIconContainer>
          )}
          <StyledFieldContainer
            className='autocomplete-field-container'
            clearButtonVisible={showChangeButton && !isAddressAutocomplete}
          >
            {showTextField() && !isAddressAutocomplete ? (
              <StyledTypography className='autocomplete-styled-text'>
                {isLoading ? <Loader /> : value?.label}
              </StyledTypography>
            ) : (
              <StyledInput
                textOverflow={isAddressAutocomplete ? 'unset' : 'ellipsis'}
                id='autocomplete-input'
                type='search'
                autoComplete='off'
                ref={inputRef}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setPopupOpen(true)}
                value={inputValue}
                placeholder={placeholder ?? 'Enter text'}
                required={!isAddressAutocomplete ? true : false}
                disabled={disabled}
              />
            )}
          </StyledFieldContainer>
          {showChangeButton && !isAddressAutocomplete && (
            <ClearContainer ref={clearContainerRef} className='autocomplete-clear-container'>
              <ClearButton onClick={handleChangeClick}>{clearLabel}</ClearButton>
            </ClearContainer>
          )}
          {!showChangeButton && endIcon !== undefined && (
            <EndIconContainer isClickable={submitOnEndIconClick} onClick={handleEndIconClick}>
              {endIcon}
            </EndIconContainer>
          )}
        </MainContainer>
        {(!helperTextPosition || helperTextPosition === 'relative') && helperText && (
          <Box>{helperText}</Box>
        )}
        {!disabled && popupOpen && options.length > 0 && (
          <StyledOptions
            position={popupPosition ?? 'absolute'}
            positionOffset={
              mainContainerRef.current?.offsetHeight
                ? mainContainerRef.current?.offsetHeight + 'px'
                : '0px'
            }
            paddingTopAndBottom={isCheckoutFlow}
          >
            {helperTextPosition === 'absolute' && helperText && <Box>{helperText}</Box>}
            {options
              .filter((e1) => e1.groupLabel !== HELPER_TEXT)
              .map((e1, i) => (
                <OptionItemContainer key={e1.id} paddingRequired={isCheckoutFlow}>
                  {e1.groupLabel !== ERROR && <GroupHeader>{e1.groupLabel}</GroupHeader>}
                  <StyledLi className='autocomplete-dropdown'>
                    {e1.groupItems.map((e2, j) => {
                      switch (e2.type) {
                        case AutocompleteOptionType.STANDARD:
                          return (
                            <GroupItem
                              onMouseEnter={() => setActiveOption({ groupIndex: i, itemIndex: j })}
                              className='autocomplete-dropdown-item'
                              key={e2.id}
                              onClick={() => handleSelect(e2)}
                              isActive={
                                activeOption?.groupIndex === i && activeOption?.itemIndex === j
                              }
                            >
                              {typeof e2.label === 'string' ? (
                                <span dangerouslySetInnerHTML={sanitizedData(e2.label)} />
                              ) : (
                                e2.label
                              )}
                            </GroupItem>
                          );
                        case AutocompleteOptionType.JSX:
                          return <React.Fragment key={e2.id}>{e2.element}</React.Fragment>;
                        default:
                          return <></>;
                      }
                    })}
                  </StyledLi>
                </OptionItemContainer>
              ))}{' '}
            {isCheckoutFlow && hideHelperTextContainer !== true && (
              <HelperTextContainer>
                {options
                  .filter((group) => group.groupLabel?.toLowerCase().includes(HELPER_TEXT))
                  .map((group, index) => (
                    <div key={index}>
                      {group.groupItems.map((item) => (
                        <React.Fragment key={item.id}>
                          {item.type === AutocompleteOptionType.JSX ? item.element : null}
                        </React.Fragment>
                      ))}
                    </div>
                  ))}
              </HelperTextContainer>
            )}
          </StyledOptions>
        )}
      </RootContainer>
    </ClickAwayListener>
  );
}

export default Autocomplete;
